import { assigneesOf, isNpc } from './derive';
import { KST, formatClock, formatDay, tzOffsetMinutes, workWindowsOf } from './time';

/**
 * W7 Dashboard — 보드 데이터에서 화면 값을 계산한다 (저장하는 값 없음).
 * 근거: MVP #18 막힘 · #49 팀원 상태 · #52 팀 진행률 · #53 workload · #54 인계 체인 · 기한 준수 §2 · §4 · §9 · §11.
 *
 *   시간 구조   T0 = project.startAt(리더 시작) · 팀원 시작 = T0 + 1h · 종료 = project.endAt (T0 + 49h)
 *   릴레이 단계  선행 관계 깊이로 태스크를 정렬 → 같은 깊이에서 직무별로 묶음 → 바로 앞 깊이와 직무가 같으면 합침.
 *              취소 · 거절 제외. 이름 Designer = Design · Developer = Build · PM = 첫 단계 Plan, 그 뒤 QA
 *   단계 완료   묶인 태스크가 모두 승인(또는 팀장에게 제출) — 완료 시각 = 승인된 버전 제출 시각 중 가장 늦은 값
 *   보정 기한   due + 앞 단계 지연(최댓값) + 되돌림 대기(승인 전에는 지금까지 · 잠정) — 종료를 넘지 않음
 *   검토 중     모두 제출되고 승인만 남음 → 지연 계산을 마지막 제출 시각에서 멈춘다
 *   종료 뒤     지연은 종료 시각에서 멈춘다 — 끝내지 못한 태스크는 Not handed off
 *   히어로 그래프 태스크 1개 = 막대 1줄 (만든 순서). 칸 = 그때 들고 있던 사람 (담당자 → 검토자 → 반려면 다시 담당자)
 *
 * 시각은 ms. 화면 문구는 KST.
 */

export const MIN = 60_000;
const HOUR = 60 * MIN;
const NPC = 'npc';

const JOB = { planner: 'PM', designer: 'Designer', developer: 'Developer', marketer: 'Marketer' };
/** 직무 순서 · 색 (시안 dashboard.js JOBS — PM olive03 · Designer purple03 · Developer beige03, 팀장 흰색). Marketer 는 시안에 없어 orange03 */
const JOB_ORDER = ['planner', 'designer', 'developer', 'marketer'];
export const JOB_COLOR = {
  planner: '#dbd10e',
  designer: '#e3b5f9',
  developer: '#e9e0ba',
  marketer: '#ffa100',
  lead: '#FFFFFF',
};
export const JOB_LABEL = { ...JOB, lead: 'Team lead Kim' };
const STAGE_NAME = { Designer: 'Design', Developer: 'Build' };

const ms = (iso) => (iso ? new Date(iso).getTime() : null);
const pad = (n) => String(n).padStart(2, '0');
export const hm = (t) => formatClock(new Date(t), KST);
export const kstAt = (t) => `${formatDay(new Date(t), KST)}, ${hm(t)} KST`;
const kstShort = (t) => `${formatDay(new Date(t), KST)}, ${hm(t)}`;
export const dur = (m) => {
  const v = Math.max(0, Math.round(m));
  const h = Math.floor(v / 60);
  const r = v % 60;
  return h ? (r ? `${h}h ${r}m` : `${h}h`) : `${r}m`;
};
export const hms = (s) => {
  const v = Math.max(0, Math.floor(s));
  return `${pad(Math.floor(v / 3600))}:${pad(Math.floor(v / 60) % 60)}:${pad(v % 60)}`;
};
const first = (name) => (name ?? '').split(' ')[0];
const ini = (name) =>
  (name ?? '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
const jobOf = (m) => JOB[m?.job] ?? m?.job ?? '';

/* ── 태스크 시각 ─────────────────────────────────────── */

/** 버전 [{ d: 결과물, r: 검토 }] — 이전 버전들 + 지금 버전 */
const versionsOf = (t) => [
  ...(t.history ?? []).map((h) => ({ d: h.deliverable, r: h.review })),
  ...(t.deliverable ? [{ d: t.deliverable, r: t.review }] : []),
];
const lastSubmit = (t) => {
  const v = versionsOf(t);
  return ms(v[v.length - 1]?.d?.submittedAt);
};
/** 단계에서 끝난 태스크 — 승인됐거나 팀장에게 제출(그때 인계 완료, Final review 시작) */
const isDoneT = (t) => t.state === 'approved' || (t.state === 'submitted' && t.receiverId === NPC);
/** 끝난 태스크의 완료 시각 — 승인된 버전 제출 시각 (팀장에게 낸 것은 그 제출 시각) */
const doneAt = (t) => ms(t.approvedSubmittedAt) ?? lastSubmit(t);
/**
 * 되돌림 — 되돌린 시각 → 수정본 제출. 승인되면 확정, 아니면 지금까지(잠정).
 * @returns [{ w: 분, prov }]
 */
const returnsOf = (t, now) => {
  const v = versionsOf(t);
  return v.flatMap((x, i) => {
    if (!x.r?.returned || !x.r.at) return [];
    const at = ms(x.r.at);
    if (at > now) return [];
    const resub = ms(v[i + 1]?.d?.submittedAt);
    const fin = t.state === 'approved' && resub != null && resub <= now;
    return [{ w: ((fin ? resub : now) - at) / MIN, prov: !fin }];
  });
};

/* ── 릴레이 단계 ─────────────────────────────────────── */

export const stagesFor = (tasks, memberById, now) => {
  const live = tasks.filter((t) => t.state !== 'cancelled' && t.state !== 'declined');
  const byId = new Map(live.map((t) => [t.id, t]));
  const job = (t) => jobOf(memberById.get(assigneesOf(t)[0]));
  const depth = new Map();
  const lv = (t, seen = new Set()) => {
    if (depth.has(t.id)) return depth.get(t.id);
    if (seen.has(t.id)) return 0;
    seen.add(t.id);
    const d = (t.predecessorIds ?? [])
      .map((id) => byId.get(id))
      .filter(Boolean)
      .reduce((m, p) => Math.max(m, lv(p, seen) + 1), 0);
    depth.set(t.id, d);
    return d;
  };
  const groups = [];
  [...new Set(live.map((t) => lv(t)))]
    .sort((a, b) => a - b)
    .forEach((L) => {
      const at = live.filter((t) => lv(t) === L);
      [...new Set(at.map(job))].forEach((j) => {
        const ts = at.filter((t) => job(t) === j);
        const last = groups[groups.length - 1];
        if (last && last.job === j && last.lvl === L - 1) {
          last.ts.push(...ts);
          last.lvl = L;
        } else groups.push({ job: j, lvl: L, ts: [...ts] });
      });
    });

  const S = groups.map((g, i) => {
    const allDone = g.ts.every(isDoneT);
    const names = [...new Set(g.ts.flatMap((t) => assigneesOf(t)))].map(
      (id) => memberById.get(id)?.name ?? 'Someone',
    );
    return {
      k: `st${i}`,
      i,
      name: STAGE_NAME[g.job] ?? (i === 0 ? 'Plan' : 'QA'),
      job: g.job,
      who: names,
      whoIds: [...new Set(g.ts.flatMap((t) => assigneesOf(t)))],
      due: Math.max(...g.ts.map((t) => ms(t.dueAt))),
      tasks: g.ts.map((t) => t.id),
      ts: g.ts,
      d: allDone ? Math.max(...g.ts.map((t) => doneAt(t) ?? 0)) : null,
      rev: !allDone && g.ts.every((t) => isDoneT(t) || t.state === 'submitted'),
      revAt: Math.max(0, ...g.ts.map((t) => lastSubmit(t) ?? 0)),
      recv: [
        ...new Set(
          g.ts.map((t) =>
            t.receiverId === NPC ? 'Team lead Kim' : (memberById.get(t.receiverId)?.name ?? ''),
          ),
        ),
      ].filter(Boolean),
    };
  });
  S.forEach((s, i) => {
    const preds = new Set(s.ts.flatMap((t) => t.predecessorIds ?? []));
    const prev = S.slice(0, i).filter((p) => p.tasks.some((id) => preds.has(id)));
    const doneP = prev.flatMap((p) => p.ts.filter(isDoneT));
    s.delayIn = Math.max(0, ...doneP.map((t) => ((doneAt(t) ?? 0) - ms(t.dueAt)) / MIN));
    const rets = prev.flatMap((p) => p.ts.flatMap((t) => returnsOf(t, now)));
    s.wait = rets.reduce((x, r) => x + r.w, 0);
    s.waitProv = rets.some((r) => r.prov);
    s.prevName =
      prev
        .filter((p) => p.ts.some((t) => isDoneT(t) && (doneAt(t) ?? 0) > ms(t.dueAt)))
        .map((p) => p.name)
        .join(' and ') ||
      prev[0]?.name ||
      '';
  });
  return S;
};

/* ── Team progress (#52) ─────────────────────────────── */

export const CATS = [
  ['final', 'Final submitted', '#0E6B4A'],
  ['hand', 'Handed off', '#71E6B5'],
  ['prog', 'In progress', '#676B67'],
  ['rework', 'Changes requested', '#FFCB45'],
  ['blocked', 'Blocked', '#FF4D5E'],
  ['accept', 'To accept', '#E4E7E2'],
];
/** 분류 — 우선순위대로 첫 매칭. 취소 · 거절은 집계하지 않는다 */
export const catOf = (t) => {
  if (t.state === 'cancelled' || t.state === 'declined') return null;
  if (t.isBlocked) return 'blocked';
  if (t.state === 'in_progress') return 'prog';
  if (t.state === 'rework_requested') return 'rework';
  if (t.state === 'assigned') return 'accept';
  return t.receiverId === NPC ? 'final' : 'hand';
};

/* ── 전체 ────────────────────────────────────────────── */

/**
 * @param data      보드 데이터
 * @param now       Date
 * @param memberById Map
 * @param needActions Inbox Needs action 중 미처리 항목 (보는 사람만)
 */
export const buildDashboard = ({ data, now: nowDate, memberById, needActions = [] }) => {
  const now = nowDate.getTime();
  const T0 = ms(data.project.startAt);
  const START = ms(data.project.teamStartAt) ?? T0 + HOUR;
  const END = ms(data.project.endAt);
  // 팀장(NPC)은 멤버 id 가 따로 있다(role 'npc') — 계산은 모두 NPC 하나로 본다
  const npcIds = new Set((data.members ?? []).filter(isNpc).map((m) => m.id));
  const toNpc = (id) => (npcIds.has(id) ? NPC : id);
  const tasks = (data.tasks ?? []).map((t) =>
    npcIds.has(t.receiverId) ? { ...t, receiverId: NPC } : t,
  );
  const viewerId = data.viewerId;

  // ── Time left
  const preT0 = now < T0;
  const setup = !preT0 && now < START;
  const pre = now < START;
  const ended = now >= END;
  // 늦음 · 막대 끝은 종료 시각에서 멈춘다 — 끝난 뒤에 늦은 시간이 더 늘지 않게
  const cur = Math.min(now, END);
  const leftM = (END - now) / MIN;
  const lvl = ended
    ? 'end'
    : preT0
      ? 'pre'
      : setup
        ? 'setup'
        : leftM <= 60
          ? 'danger'
          : leftM <= 180
            ? 'warn'
            : 'ok';
  const chipLabel = {
    warn: 'Under 3h left',
    danger: 'Under 1h left',
    end: 'Project ended',
    setup: 'Leader setup',
    pre: 'Not started',
  }[lvl];
  const target = preT0 ? T0 : END;
  const clock = {
    lvl,
    chip: chipLabel ?? null,
    // 끝 시각 — 화면이 1초마다 남은 시간을 다시 센다 (끝나면 멈춘다)
    target: ended ? null : target,
    cap: ended
      ? `Ended ${kstAt(END)}`
      : preT0
        ? `until the project starts at ${kstAt(T0)}`
        : setup
          ? `left · members join at ${hm(START)}`
          : `Ends ${kstAt(END)}`,
    sub: preT0
      ? `Ends ${kstAt(END)}`
      : ended
        ? `Ran ${kstShort(T0)} → ${kstAt(END)}`
        : `${dur((now - T0) / MIN)} since the start · ${kstAt(T0)}`,
  };
  const status = preT0
    ? `Starts ${kstAt(T0)}`
    : setup
      ? `Leader setup · members join ${hm(START)}`
      : ended
        ? `Ended ${kstAt(END)}`
        : `Live · Day ${now < START + 24 * HOUR ? 1 : 2} of 2`;

  // ── 히어로 (W7 Dashboard Version B · hand-off bars) — 시안 dashboard.js 의 계산을 실제 보드 데이터로 옮겼다
  // 축: 팀원 시작 → 종료 (48h)
  const AX = Math.max(1, END - START);
  const X = (t) => ((Math.min(Math.max(t, START), END) - START) / AX) * 100;
  const comments = data.comments ?? [];
  const openBlocker = (t) =>
    comments
      .filter((c) => c.taskId === t.id && c.type === 'blocker' && !c.parentId && !c.resolvedAt && !c.deletedAt)
      .sort((x, y) => ms(x.createdAt) - ms(y.createdAt))[0];
  const liveT = tasks.filter((t) => t.state !== 'cancelled' && t.state !== 'declined');
  const tById = new Map(liveT.map((t) => [t.id, t]));
  const nameOf = (id) => (id === NPC ? 'Team lead Kim' : (memberById.get(id)?.name ?? 'Someone'));
  const ownerOf = (t) => assigneesOf(t).map(nameOf).join(', ');
  const recvOf = (t) => (t.receiverId ? nameOf(t.receiverId) : 'Team lead Kim');
  const recvShort = (t) => (t.receiverId === NPC || !t.receiverId ? 'Team lead Kim' : first(recvOf(t)));
  const dayHm = (t) => `${formatDay(new Date(t), KST)} · ${hm(t)}`;

  // 조정 마감 (기한 준수 §4) — 기한 + 끝난 앞 태스크의 가장 큰 지연 + 앞 태스크 되돌림 대기 (고쳐 승인되기 전에는 잠정)
  const RF = new Map();
  const refOf = (t) => {
    if (RF.has(t.id)) return RF.get(t.id);
    const due = ms(t.dueAt);
    const preds = (t.predecessorIds ?? []).map((id) => tById.get(id)).filter(Boolean);
    const dp = preds.filter((p) => isDoneT(p) && doneAt(p) != null && doneAt(p) <= cur);
    const late = (p) => (doneAt(p) - ms(p.dueAt)) / MIN;
    const delay = Math.max(0, ...dp.map(late));
    const rets = preds.flatMap((p) => returnsOf(p, cur));
    const wait = rets.reduce((a, r) => a + r.w, 0);
    const adj = delay + wait > 0 ? Math.min(END, due + (delay + wait) * MIN) : null;
    const r = {
      adj,
      ref: adj ?? due,
      delay,
      wait,
      prov: rets.some((x) => x.prov),
      lateBy: delay > 0 ? dp.find((p) => late(p) === delay) : null,
    };
    RF.set(t.id, r);
    return r;
  };

  // 태스크 1개 = 블록 1개 — 수락 전은 계획(점선), 수락 뒤는 수락 → 넘김까지
  const blocks = liveT.map((t) => {
    const r = refOf(t);
    const owner = memberById.get(assigneesOf(t)[0]);
    const done = isDoneT(t);
    const accAt = ms(t.acceptedAt) ?? ms(t.assignedAt) ?? START;
    const accd = t.state !== 'assigned' && accAt != null;
    const review = !done && t.state === 'submitted';
    const preds = (t.predecessorIds ?? []).map((id) => tById.get(id)).filter(Boolean);
    const sub = done ? doneAt(t) : lastSubmit(t);
    let kind;
    let s;
    let bE = r.ref;
    let lE = null;
    if (!accd) {
      kind = 'plan';
      s = Math.min(preds.length ? Math.max(...preds.map((p) => refOf(p).ref)) : START, r.ref - 30 * MIN);
      if (!pre && cur > r.ref) lE = cur;
    } else {
      s = accAt;
      if (done) {
        kind = 'done';
        bE = Math.min(sub ?? cur, r.ref);
        if (sub > r.ref) lE = sub;
      } else if (review) {
        kind = 'review';
        if (sub > r.ref) lE = sub;
      } else {
        kind = ended ? 'open' : 'active';
        if (cur > r.ref) lE = cur;
      }
    }
    bE = Math.max(bE, s + 10 * MIN);
    const late = lE != null && lE > r.ref;
    const st =
      kind === 'done'
        ? 'Done'
        : kind === 'review'
          ? 'In review'
          : kind === 'plan'
            ? pre
              ? 'Planned'
              : 'To accept'
            : kind === 'open'
              ? 'Not handed off'
              : t.isBlocked
                ? 'Blocked'
                : t.state === 'rework_requested'
                  ? 'Changes requested'
                  : 'In progress';
    return {
      t,
      id: t.id,
      jk: owner?.job ?? '',
      job: jobOf(owner),
      r,
      kind,
      s0: s,
      bE,
      lE,
      e0: Math.max(bE, lE ?? 0),
      late,
      lateM: late ? (lE - r.ref) / MIN : 0,
      inPlay: accd && !done && !ended && !pre,
      st,
      preds: preds.map((p) => p.id),
    };
  });
  blocks.forEach((b) => {
    b.next = blocks.filter((x) => x.preds.includes(b.id)).map((x) => x.id);
  });

  // 담당자 칸 — 넘길 때마다 나눈다: 담당자 working → 받는 사람 reviewing → (반려 · 되돌림) 담당자 reworking → … → 승인
  const partsOf = (b) => {
    const t = b.t;
    if (b.kind === 'plan') return [];
    const owner = assigneesOf(t)[0];
    const recv = t.receiverId ?? NPC;
    const parts = [];
    let who = owner;
    let role = 'work';
    let from = b.s0;
    let closed = false;
    versionsOf(t).forEach((x) => {
      if (closed) return;
      const sub = ms(x.d?.submittedAt);
      if (sub == null || sub > cur || sub <= from) return;
      parts.push({ who, role, from, to: sub });
      from = sub;
      who = recv;
      role = 'review';
      const rAt = ms(x.r?.at);
      if (rAt == null || rAt > cur) return;
      // 팀장에게 낸 것은 바로 승인된다 — 검토 칸은 되돌릴 때만 남는다
      if (rAt > from) parts.push({ who, role, from, to: rAt });
      from = rAt;
      if (x.r.result === 'approved') closed = true;
      else {
        who = owner;
        role = 'rework';
      }
    });
    if (!closed && b.kind !== 'done') parts.push({ who, role, from, to: Math.max(from + 10 * MIN, cur), open: !ended });
    if (!parts.length) parts.push({ who: owner, role: 'work', from: b.s0, to: Math.max(b.bE, b.s0 + 10 * MIN) });
    return parts;
  };
  const ROLE = { work: 'Working', review: 'Reviewing', rework: 'Reworking' };
  const holderJk = (id) => (id === NPC ? 'lead' : (memberById.get(id)?.job ?? ''));
  const nLate = blocks.filter((b) => b.late).length;
  const play = blocks.filter((b) => b.inPlay);
  // 가장 급한 바통 — 진행 중이 검토 중보다 먼저 → 늦은 것(많이 늦은 순) → 가까운 조정 마감 순
  const rank = (b) => [b.kind === 'review' ? 1 : 0, b.late ? 0 : 1, b.late ? -b.lateM : b.r.ref];
  const urgent =
    play.slice().sort((a, z) => {
      const A = rank(a);
      const B = rank(z);
      return A[0] - B[0] || A[1] - B[1] || A[2] - B[2];
    })[0] ?? null;

  const bars = blocks.map((b) => {
    const t = b.t;
    const parts = partsOf(b);
    const end = parts.length ? parts[parts.length - 1].to : b.bE;
    const lastWho = parts.length ? parts[parts.length - 1].who : assigneesOf(t)[0];
    const doneLike = b.kind === 'done';
    const lateL = X(Math.max(b.r.ref, b.s0));
    return {
      id: b.id,
      title: t.title,
      kind: b.kind,
      st: b.st,
      done: doneLike,
      plan: b.kind === 'plan',
      jk: b.jk,
      ini: ini(nameOf(assigneesOf(t)[0])),
      L: X(b.s0),
      planW: Math.max(0.6, X(b.bE) - X(b.s0)),
      endX: Math.max(X(Math.max(end, b.lE ?? 0)), X(b.r.ref)),
      // 남은 시간 꼬리 — 진행 중이고 조정 마감이 아직이면 지금 → 마감 점선
      ghost:
        b.kind !== 'plan' && !doneLike && !ended && b.r.ref > cur
          ? { L: X(end), W: Math.max(0, X(b.r.ref) - X(end)), jk: holderJk(lastWho) }
          : null,
      late: b.late,
      lateTxt: b.late ? ` +${dur(b.lateM)}` : '',
      tail: b.late ? { L: lateL, W: Math.max(0.4, X(b.lE) - lateL) } : null,
      urgent: urgent?.id === b.id,
      rel: [...b.preds, ...b.next],
      parts: parts.map((g, k) => ({
        k: `${b.id}-${k}`,
        L: X(g.from),
        W: Math.max(0.4, X(g.to) - X(g.from)),
        jk: holderJk(g.who),
        ini: g.who === NPC ? 'TK' : ini(nameOf(g.who)),
        // 짧은 카드 — 태스크 · 사람 · 하던 일 · 시간 (늦었으면 늦음)
        card: {
          title: t.title,
          who: `${nameOf(g.who)} · ${ROLE[g.role]}`,
          when: `${hm(g.from)} – ${g.open ? 'now' : hm(g.to)} · ${dur(((g.open ? cur : g.to) - g.from) / MIN)}`,
          late: b.late ? `+${dur(b.lateM)} late` : '',
        },
      })),
      // 막대 전체(계획 · 꼬리) 카드 — 담당자 · 상태 · 마감
      card: {
        title: t.title,
        who: `${ownerOf(t)} · ${b.st}`,
        when: `Due ${hm(b.r.ref)}${b.r.adj ? ` (was ${hm(ms(t.dueAt))})` : ''}`,
        late: b.late ? `+${dur(b.lateM)} late` : '',
      },
    };
  });

  // 모바일 — 직무 카드 (요약 + 48h 미니 막대) → 누르면 그 직무 태스크 바텀시트
  const jobCards = JOB_ORDER.map((jk) => {
    const bs = blocks.filter((b) => b.jk === jk).sort((a, z) => a.s0 - z.s0 || a.e0 - z.e0);
    if (!bs.length) return null;
    const nD = bs.filter((b) => b.kind === 'done').length;
    const nA = bs.filter((b) => b.inPlay).length;
    const nO = bs.filter((b) => b.kind === 'open').length;
    const nL = bs.filter((b) => b.late).length;
    return {
      jk,
      job: JOB[jk],
      sum: `${nD} done · ${ended ? `${nO} not handed off` : `${nA} active`}`,
      lateTxt: nL ? `${nL} late` : '',
      mini: bs.flatMap((b) => {
        const L = X(b.s0);
        const it = [{ k: b.id, L, W: Math.max(0.8, X(b.bE) - L), kind: b.kind }];
        if (b.late) {
          const tl = X(Math.max(b.r.ref, b.s0));
          it.push({ k: `${b.id}-late`, L: tl, W: Math.max(0.8, X(b.lE) - tl), kind: 'late' });
        }
        return it;
      }),
      rows: bs.map((b) => ({
        id: b.id,
        title: b.t.title,
        who: `${ownerOf(b.t)} → ${recvOf(b.t)}`,
        st: b.st + (b.late ? ` · +${dur(b.lateM)}` : ''),
        tone: b.late ? 'late' : b.kind === 'done' ? 'done' : '',
        due: `Due ${dayHm(b.r.ref)}${b.r.adj ? ` · was ${hm(ms(b.t.dueAt))}` : ''}`,
      })),
    };
  }).filter(Boolean);

  // 바통 자리 — 수락했고 아직 넘기지 않은 태스크(진행 중 · 수정 요청 · 검토 중) 중 가장 급한 것
  const why2 = (r) =>
    [
      r.delay > 0 && r.lateBy ? `“${r.lateBy.title}” ran ${dur(r.delay)} late` : '',
      r.wait > 0
        ? r.prov
          ? `a returned task has waited ${dur(r.wait)} for its fix so far`
          : `a returned task took ${dur(r.wait)} to fix`
        : '',
    ]
      .filter(Boolean)
      .join(' and ');
  const whyS = (r) =>
    [
      r.delay > 0 ? `an earlier task ran ${dur(r.delay)} late` : '',
      r.wait > 0 ? `a return ${r.prov ? `has waited ${dur(r.wait)} so far` : `took ${dur(r.wait)}`}` : '',
    ]
      .filter(Boolean)
      .join(' and ');
  const openB = blocks.filter((b) => b.kind === 'open');
  const planB = blocks.filter((b) => b.kind === 'plan').sort((a, z) => a.r.ref - z.r.ref);
  const npcDone = blocks
    .filter((b) => b.kind === 'done' && (b.t.receiverId ?? NPC) === NPC)
    .sort((a, z) => doneAt(z.t) - doneAt(a.t))[0];
  const firstB = blocks.slice().sort((a, z) => a.r.ref - z.r.ref)[0];
  const E0 = { ini: '', devLine: '', dev: '' };
  let hero;
  if (urgent) {
    const u = urgent;
    const rv = u.kind === 'review';
    const blk = u.t.isBlocked ? openBlocker(u.t) : null;
    hero = {
      label: `${play.length} ${play.length === 1 ? 'baton' : 'batons'} in play · most urgent`,
      title: u.t.title,
      ini: ini(ownerOf(u.t)),
      ownerLine: `${ownerOf(u.t)} → ${recvOf(u.t)}`,
      handTitle: rv ? `Submitted to ${recvShort(u.t)} at` : `Hand off to ${recvShort(u.t)} by`,
      handAt: hm(rv ? lastSubmit(u.t) : u.r.ref),
      inT: rv
        ? u.late
          ? `${dur(u.lateM)} late`
          : 'on time'
        : u.late
          ? `${dur(u.lateM)} over`
          : `in ${dur((u.r.ref - now) / MIN)}`,
      trackLine: blk
        ? `Blocked ${dur((now - ms(blk.createdAt)) / MIN)}`
        : rv
          ? 'In review'
          : u.late
            ? `Late · ${dur(u.lateM)}`
            : 'On track',
      tone: blk || u.late ? 'crit' : 'ok',
      devLine: u.r.adj ? `Was ${hm(ms(u.t.dueAt))} · ${whyS(u.r)}` : '',
      dev: u.r.adj
        ? `Due moved ${hm(ms(u.t.dueAt))} → ${hm(u.r.ref)} because ${why2(u.r)}`
        : 'No schedule changes',
    };
  } else if (pre) {
    hero = {
      ...E0,
      label: `${planB.length} tasks to accept`,
      title: setup ? 'Leader setup' : 'Not started',
      ini: firstB ? ini(ownerOf(firstB.t)) : '',
      ownerLine: firstB ? `${ownerOf(firstB.t)} starts “${firstB.t.title}”` : '',
      handTitle: 'First hand-off by',
      handAt: firstB ? hm(firstB.r.ref) : '—',
      inT: firstB ? `in ${dur((firstB.r.ref - now) / MIN)}` : '',
      trackLine: `Starts ${hm(START)}`,
      tone: 'quiet',
    };
  } else if (ended) {
    hero = {
      ...E0,
      label: '0 batons in play',
      title: 'Project ended',
      ownerLine: openB.length
        ? `${openB.length} ${openB.length === 1 ? 'task was' : 'tasks were'} not handed off`
        : 'Every task was handed off',
      handTitle: npcDone ? 'Team lead Kim received it at' : 'Team lead Kim received nothing',
      handAt: npcDone ? hm(doneAt(npcDone.t)) : '—',
      inT: '',
      trackLine: openB.length ? `Not handed off · ${openB.map((b) => b.t.title).join(', ')}` : 'Handed off',
      tone: openB.length ? 'crit' : 'quiet',
    };
  } else {
    const nx = planB[0];
    hero = {
      ...E0,
      label: '0 batons in play',
      title: 'No batons in play',
      ownerLine: nx ? `Next: “${nx.t.title}” · ${ownerOf(nx.t)}` : 'Every task is handed off',
      handTitle: nx ? 'Next hand-off by' : '',
      handAt: nx ? hm(nx.r.ref) : '',
      inT: nx && nx.r.ref > now ? `in ${dur((nx.r.ref - now) / MIN)}` : '',
      trackLine: nx ? 'Waiting to be accepted' : 'Handed off',
      tone: 'quiet',
    };
  }
  const sentence = urgent
    ? `${ownerOf(urgent.t)} (${urgent.job}) holds “${urgent.t.title}”. ` +
      (urgent.kind === 'review'
        ? `Submitted and waiting for ${recvOf(urgent.t)}’s review.`
        : `Hand off to ${recvOf(urgent.t)} by ${kstAt(urgent.r.ref)}.`)
    : hero.ownerLine;

  // 시간 눈금 — 6시간마다 (태블릿은 12시간), 글자는 12시간마다 · 날짜는 하루마다
  const ticksFor = (step) => {
    const out = [];
    for (let h = 0; h <= 48; h += step) {
      const at = START + h * HOUR;
      out.push({
        x: (h / 48) * 100,
        label: h % 12 ? '' : h % 24 ? hm(at) : dayHm(at),
        major: h % 12 === 0,
        edge: h === 0 ? 'start' : h === 48 ? 'end' : 'mid',
      });
    }
    return out;
  };
  const ticks = ticksFor(6);
  const ticksTab = ticksFor(12);

  // ── Need actions (보는 사람만)
  const na = {
    count: needActions.length,
    rows: needActions.slice(0, 3),
  };

  // ── Team progress
  const counts = Object.fromEntries(CATS.map(([k]) => [k, 0]));
  tasks.forEach((t) => {
    const c = catOf(t);
    if (c) counts[c] += 1;
  });
  const total = Object.values(counts).reduce((a, x) => a + x, 0);
  const finalDen = tasks.filter((t) => t.receiverId === NPC && catOf(t)).length;
  const maxC = Math.max(1, ...Object.values(counts));
  const prog = {
    big: `${counts.final} of ${finalDen}`,
    hand: `${counts.hand} handed off`,
    foot: `${total} tasks · cancelled and declined not counted`,
    cols: CATS.map(([k, label, color]) => ({ k, label, color, n: counts[k], h: (counts[k] / maxC) * 100 })),
  };

  // ── Who needs help — 막힌 태스크 1개 = 1행 (가장 오래된 열린 막힘 댓글)
  const impactOf = (t) => {
    const later = tasks.find(
      (x) => !['cancelled', 'declined'].includes(x.state) && (x.predecessorIds ?? []).includes(t.id),
    );
    return later ? `“${later.title}” can’t be submitted until this is approved` : '';
  };
  const blkRows = tasks
    .filter((t) => t.isBlocked)
    .map((t) => {
      const c = openBlocker(t);
      const byId = c?.authorId ?? assigneesOf(t)[0];
      const at = c ? ms(c.createdAt) : now;
      const m = memberById.get(byId);
      const asked = (c?.targetUserIds?.length ? c.targetUserIds : c?.targetUserId ? [c.targetUserId] : [])
        .map((id) => (toNpc(id) === NPC ? 'Team lead Kim' : first(memberById.get(id)?.name)))
        .filter(Boolean);
      return {
        taskId: t.id,
        commentId: c?.id ?? null,
        at,
        byId,
        name: m?.name ?? 'Someone',
        isMe: byId === viewerId,
        task: t.title,
        body: c?.body ?? 'Marked the task as blocked.',
        impact: impactOf(t),
        wait: dur((now - at) / MIN),
        ask: asked.length ? `Asked ${asked.join(', ')}` : '',
      };
    })
    .sort((x, y) => x.at - y.at);

  // ── Team now
  const span = Math.max(1, END - now);
  const X2 = (t) => ((t - now) / span) * 100;
  const people = (data.members ?? []).filter((m) => !isNpc(m));
  const windowsFor = (m) =>
    m.timezone && !ended ? workWindowsOf(now, m, Math.ceil(span / (24 * HOUR)) + 1) : [];
  const offsetOf = (m) => (m.timezone ? tzOffsetMinutes(nowDate, m.timezone) : 540);
  const offL = (o) => {
    const a = Math.abs(o);
    return `UTC${o < 0 ? '−' : '+'}${Math.floor(a / 60)}${a % 60 ? `:${pad(a % 60)}` : ''}`;
  };
  const diffL = (o) => {
    const d = o - 540;
    if (!d) return 'Same as Seoul';
    const a = Math.abs(d);
    return `Seoul ${d < 0 ? '−' : '+'}${Math.floor(a / 60)}h${a % 60 ? ` ${a % 60}m` : ''}`;
  };
  const localHm = (m) => formatClock(nowDate, m.timezone || KST);
  const curTask = (m) => {
    const t = tasks.find(
      (x) => assigneesOf(x).includes(m.id) && ['in_progress', 'rework_requested'].includes(x.state),
    );
    if (t)
      return `${t.title} · ${t.isBlocked ? 'Blocked' : t.state === 'rework_requested' ? 'Changes requested' : 'In progress'}`;
    const r = tasks.find((x) => x.receiverId === m.id && x.state === 'submitted');
    return r ? `${r.title} · Reviewing` : 'No task in progress';
  };
  const hoursText = (m) =>
    (m.availableSlots?.length
      ? m.availableSlots.map((x) => `${x.from}–${x.to}`).join(', ')
      : m.availableFrom && m.availableTo
        ? `${m.availableFrom}–${m.availableTo}`
        : 'Any time') + ' local';

  // 넘기지 않은 태스크 중 조정 마감이 남은 것 (팀장 반려도 rework_requested 로 돌아와 여기에 든다)
  const openT = liveT
    .filter((t) => !isDoneT(t))
    .map((t) => ({ t, ref: refOf(t).ref }))
    .filter((x) => x.ref > now && x.ref < END)
    .sort((a, z) => a.ref - z.ref);
  // Team now 마감 표시 — 마감 시각마다 점선 1개. 라벨 배치(+n 묶기)는 화면 폭을 아는 쪽에서
  const lDues = [...new Set(openT.map((x) => x.ref))].map((r) => ({
    k: String(r),
    x: X2(r),
    at: hm(r),
    titles: openT.filter((y) => y.ref === r).map((y) => y.t.title),
  }));
  const lanes = people.map((m) => {
    const name = m.name;
    const tag = m.role === 'leader' ? 'Leader' : m.staff ? 'Staff' : '';
    const base = { id: m.id, name, ini: ini(name), job: jobOf(m) + (tag ? ` · ${tag}` : ''), isYou: m.id === viewerId };
    if (m.status === 'dropped') {
      return { ...base, left: true, time: 'Left', statusT: 'Left the project', tone: 'quiet', wins: [], cap: '' };
    }
    const wins = windowsFor(m)
      .map(([s, e]) => [Math.max(s, now), Math.min(e, END)])
      .filter(([s, e]) => e > s);
    const hlMin = wins.reduce((a, [s, e]) => a + (e - s), 0) / MIN;
    const online = !ended && (!m.timezone || wins.some(([s, e]) => s <= now && now < e));
    const back = !online && !ended && wins.length ? wins.find(([s]) => s > now)?.[0] : null;
    const endOn = online ? (wins.find(([s, e]) => s <= now && now < e)?.[1] ?? END) : null;
    const nIn = tasks.filter(
      (t) => assigneesOf(t).includes(m.id) && ['in_progress', 'rework_requested'].includes(t.state),
    ).length;
    const bt = tasks.find((t) => t.isBlocked && assigneesOf(t).includes(m.id));
    const bc = bt ? openBlocker(bt) : null;
    // 다음 마감 — 아직 넘기지 않은 내 태스크 중 조정 마감이 가장 가까운 것
    const nextDue = openT.find((x) => assigneesOf(x.t).includes(m.id));
    const offAtDue =
      nextDue && m.timezone && !wins.some(([s, e]) => s <= nextDue.ref - MIN && nextDue.ref - MIN < e);
    let statusT;
    let tone;
    if (ended) {
      // 종료 뒤에는 막힘 · 과부하보다 종료가 먼저다
      statusT = 'Project ended';
      tone = 'quiet';
    } else if (bt) {
      statusT = `Blocked · ${dur((now - (bc ? ms(bc.createdAt) : now)) / MIN)}`;
      tone = 'crit';
    } else if (nIn >= 2) {
      statusT = `Overloaded · ${nIn} in progress`;
      tone = 'warn';
    } else if (!online) {
      statusT = ended ? 'Project ended' : hlMin > 0 && back ? `Off hours · back ${hm(back)}` : 'No hours left';
      tone = 'quiet';
    } else if (endOn && endOn - now <= 2 * HOUR && endOn < END) {
      statusT = `Goes offline at ${hm(endOn)}`;
      tone = 'warn';
    } else {
      statusT = `Available until ${hm(endOn ?? END)}`;
      tone = 'quiet';
    }
    const sub = tone !== 'crit' && offAtDue ? `Off when “${nextDue.t.title}” is due (${hm(nextDue.ref)})` : '';
    const o = offsetOf(m);
    return {
      ...base,
      left: false,
      online,
      time: `${localHm(m)} local · ${diffL(o)}`,
      wins: wins.map(([s, e]) => ({ L: X2(s), W: X2(e) - X2(s) })),
      statusT: sub || statusT,
      tone: sub ? 'warn' : tone,
      cap: `${hlMin ? dur(hlMin) : '0h'} left · ${nIn} in progress`,
      card: {
        presence: online ? 'Online' : back ? `Off hours · back at ${hm(back)} KST` : ended ? 'Project ended' : 'Offline',
        timeLine: `${localHm(m)} local · ${offL(o)} · ${diffL(o)}`,
        kstLine: `${hm(now)} KST`,
        task: curTask(m),
        hours: `${hoursText(m)} · ${hlMin ? `${dur(hlMin)} left` : 'No hours left'}`,
      },
    };
  });
  const lTicks = [];
  for (let t = Math.ceil(now / (6 * HOUR)) * 6 * HOUR; t < END - 45 * MIN; t += 6 * HOUR) {
    const x = X2(t);
    if (x >= 10 && x <= 76) lTicks.push({ x, label: hm(t) === '00:00' ? `${formatDay(new Date(t), KST)} · 00:00` : hm(t) });
  }
  return {
    status,
    statusLive: !preT0 && !ended,
    clock,
    hero,
    sentence,
    bars,
    ticks,
    nowX: X(cur),
    showNow: !pre,
    nowLabel: ended ? `Ended ${hm(END)}` : `Now ${hm(now)}`,
    nLate,
    jobCards,
    ticksTab,
    lDues,
    na,
    prog,
    blk: blkRows,
    lanes,
    lTicks,
    lNow: hm(now),
    lEnd: `${hm(END)} end`,
  };
};
